import { useState, Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@app/components/ui/table";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { ExperimentStatusBadge } from "@app/components/ExperimentStatusBadge";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  MessageSquarePlus,
  CheckCircle,
  Brain,
  BookOpen,
  CalendarClock,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { DocumentGroundingViewer } from "@app/components/DocumentGroundingViewer";
import type { GroundingEntry, GroundingField } from "@app/components/DocumentGroundingViewer";
import { GroundingHeatmap } from "@app/components/GroundingHeatmap";
import { fmtMs } from "@app/lib/format";

interface Feedback {
  id: string;
  key: string;
  score: number | null;
  value: string | null;
  comment: string | null;
  source_type: string;
  created_at: string;
}

interface MergeConflictCandidate {
  value: unknown;
  evidence?: string | null;
  chunk_idx?: number;
  chunk_max_date?: string | null;
}

interface MergeConflict {
  strategy: string;
  candidates: MergeConflictCandidate[];
  winning_value: unknown;
  refined?: boolean;
  refined_value?: unknown;
  group?: string;
}

interface FieldScoreDetail {
  f1: number;
  precision: number;
  recall: number;
  exact_match: boolean;
}

interface RunMetadata {
  field_scores?: Record<string, number>;
  field_scores_detailed?: Record<string, FieldScoreDetail>;
  llm_judge_scores?: Record<string, { score: number; reason: string }> | null;
  overall_score?: number | null;
  group_timings_ms?: Record<string, number>;
  merge_conflicts?: Record<string, MergeConflict>;
  n_merge_conflicts?: number;
  wall_ms?: number;
}

export interface ExperimentRun {
  id: string;
  example_id: string;
  status: string;
  input_data: Record<string, unknown> | null;
  actual_output: Record<string, unknown> | null;
  expected_output: Record<string, unknown> | null;
  grounding_data: Record<string, GroundingEntry> | null;
  latency_ms: number | null;
  total_tokens: number | null;
  error_message: string | null;
  feedbacks: Feedback[];
  metadata_?: RunMetadata | null;
  /** Date clinique de la ligne évaluée (AAAA-MM-JJ). Un même patient peut avoir
   * plusieurs runs, un par date ; null = ligne non datée (tous les documents). */
  as_of?: string | null;
  created_at: string;
}

interface RunsTableProps {
  runs: ExperimentRun[];
  fields?: GroundingField[];
  /** Both omitted where the laboratory is only read: the public demo. */
  onAddFeedback?: (runId: string) => void;
  onValidate?: (runId: string) => void;
}

function truncateJson(obj: unknown, maxLen: number = 60): string {
  if (obj === null || obj === undefined) return "—";
  const str = typeof obj === "string" ? obj : JSON.stringify(obj, null, 0);
  if (str.length <= maxLen) return str;
  return str.substring(0, maxLen) + "...";
}

function ScoreBadges({ feedbacks }: { feedbacks: Feedback[] }) {
  if (feedbacks.length === 0) return <span className="text-slate-400">—</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {feedbacks.map((fb) => (
        <Badge
          key={fb.id}
          variant={
            fb.score !== null && fb.score >= 0.7
              ? "default"
              : fb.score !== null && fb.score >= 0.4
                ? "secondary"
                : "destructive"
          }
          className="text-xs"
        >
          {fb.key}:{" "}
          {fb.score !== null
            ? `${(fb.score * 100).toFixed(0)}%`
            : fb.value || "—"}
        </Badge>
      ))}
    </div>
  );
}

function fmtScalar(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > 80 ? v.substring(0, 80) + "…" : v;
  if (Array.isArray(v) || typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.substring(0, 80) + "…" : s;
  }
  return String(v);
}

function FieldScoresTable({
  metadata,
  expected,
  actual,
}: {
  metadata: RunMetadata;
  expected: Record<string, unknown> | null;
  actual: Record<string, unknown> | null;
}) {
  // Sorted by F1 ascending — failing fields surface first (where reviewers
  // want to focus).
  const detailed = metadata.field_scores_detailed || {};
  const simple = metadata.field_scores || {};
  const judge = metadata.llm_judge_scores || {};
  const keys = Object.keys(detailed).length
    ? Object.keys(detailed)
    : Object.keys(simple);
  if (keys.length === 0) return null;
  const rows = keys
    .map((k) => ({
      key: k,
      f1: detailed[k]?.f1 ?? simple[k] ?? null,
      precision: detailed[k]?.precision ?? null,
      recall: detailed[k]?.recall ?? null,
      exact: detailed[k]?.exact_match ?? null,
      judge: judge[k]?.score ?? null,
    }))
    .sort((a, b) => (a.f1 ?? 0) - (b.f1 ?? 0));
  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-slate-700 mb-2">
        <Trans>Scores par champ</Trans>
      </p>
      <div className="overflow-x-auto bg-white border border-slate-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-slate-500">
              <th className="py-1.5 px-2"><Trans>Champ</Trans></th>
              <th className="py-1.5 px-2 text-right">F1</th>
              <th className="py-1.5 px-2 text-right"><Trans>Précision</Trans></th>
              <th className="py-1.5 px-2 text-right"><Trans>Rappel</Trans></th>
              <th className="py-1.5 px-2 text-right"><Trans>Exact</Trans></th>
              <th className="py-1.5 px-2 text-right"><Trans>LLM-judge</Trans></th>
              <th className="py-1.5 px-2"><Trans>Vérité</Trans></th>
              <th className="py-1.5 px-2"><Trans>Extrait</Trans></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100">
                <td className="py-1.5 px-2 font-mono text-slate-700">
                  {r.key}
                </td>
                <td
                  className={`py-1.5 px-2 text-right font-semibold ${
                    r.f1 === null
                      ? "text-slate-400"
                      : r.f1 >= 0.8
                        ? "text-green-700"
                        : r.f1 >= 0.5
                          ? "text-primary"
                          : "text-red-600"
                  }`}
                >
                  {r.f1 === null ? "—" : `${(r.f1 * 100).toFixed(0)}%`}
                </td>
                <td className="py-1.5 px-2 text-right text-slate-600">
                  {r.precision === null
                    ? "—"
                    : `${(r.precision * 100).toFixed(0)}%`}
                </td>
                <td className="py-1.5 px-2 text-right text-slate-600">
                  {r.recall === null
                    ? "—"
                    : `${(r.recall * 100).toFixed(0)}%`}
                </td>
                <td className="py-1.5 px-2 text-right">
                  {r.exact === null ? (
                    "—"
                  ) : r.exact ? (
                    <span className="text-green-700">✓</span>
                  ) : (
                    <span className="text-slate-400">✗</span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-right text-slate-600">
                  {r.judge === null
                    ? "—"
                    : `${(r.judge * 100).toFixed(0)}%`}
                </td>
                <td className="py-1.5 px-2 text-slate-600 max-w-xs truncate">
                  {fmtScalar(expected?.[r.key])}
                </td>
                <td className="py-1.5 px-2 text-slate-600 max-w-xs truncate">
                  {fmtScalar(actual?.[r.key])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MergeConflictsList({
  conflicts,
}: {
  conflicts: Record<string, MergeConflict>;
}) {
  const entries = Object.entries(conflicts);
  if (entries.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-primary" />
        <Trans>
          Conflits de fusion ({entries.length})
        </Trans>
      </p>
      <div className="space-y-2">
        {entries.map(([key, conflict]) => (
          <div
            key={key}
            className="bg-white border border-slate-200 rounded p-3 text-xs"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono font-semibold text-slate-700">
                {key}
              </span>
              <Badge variant="outline" className="text-xs">
                <Trans>Stratégie:</Trans> {conflict.strategy}
              </Badge>
            </div>
            <div className="mb-2">
              <span className="text-slate-500">
                <Trans>Valeur retenue</Trans>:{" "}
              </span>
              <span className="font-semibold text-primary">
                {fmtScalar(conflict.winning_value)}
              </span>
              {conflict.refined && (
                <Badge className="ml-2 bg-blue-50 text-primary border-primary/30 text-3xs">
                  <Trans>raffiné par LLM</Trans>
                </Badge>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-slate-500">
                <Trans>Candidats:</Trans>
              </p>
              {conflict.candidates.map((c, i) => (
                <div
                  key={i}
                  className="pl-3 border-l-2 border-slate-200 text-slate-600"
                >
                  <span className="font-medium">{fmtScalar(c.value)}</span>
                  {c.evidence && (
                    <span className="text-slate-400 italic ml-2">
                      « {c.evidence.substring(0, 100)}
                      {c.evidence.length > 100 ? "…" : ""} »
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface GroundingModalProps {
  run: ExperimentRun;
  fields: GroundingField[];
  onClose: () => void;
}

function GroundingModal({ run, fields, onClose }: GroundingModalProps) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* ``overflow-hidden`` + ``min-w-0`` clip any descendant that tries to
        * push the modal wider than ``max-w-5xl`` — historically a long source
        * quote inside SpanCard could leak past the right edge because the
        * DialogContent grid lets its children adopt their intrinsic width. */}
      <DialogContent className="max-w-5xl w-full overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4 text-primary" />
            <Trans>Sources de l'extraction</Trans>
          </DialogTitle>
        </DialogHeader>

        <DocumentGroundingViewer
          grounding={run.grounding_data ?? {}}
          fields={fields}
          extractedValues={run.actual_output ?? {}}
        />
      </DialogContent>
    </Dialog>
  );
}

export function RunsTable({ runs, fields = [], onAddFeedback, onValidate }: RunsTableProps) {
  const { t } = useLingui();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [groundingRunId, setGroundingRunId] = useState<string | null>(null);

  const groundingRun = groundingRunId
    ? runs.find((r) => r.id === groundingRunId) ?? null
    : null;

  if (runs.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>Aucun run pour cette expérience.</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead className="w-32"><Trans>Date clinique</Trans></TableHead>
            <TableHead>Entrée</TableHead>
            <TableHead>Sortie</TableHead>
            <TableHead className="w-20">Statut</TableHead>
            <TableHead className="w-20">F1</TableHead>
            <TableHead className="w-20">Conflits</TableHead>
            <TableHead className="w-20">Latence</TableHead>
            <TableHead className="w-20">Tokens</TableHead>
            <TableHead>Scores</TableHead>
            <TableHead className="w-28">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run, idx) => (
            <Fragment key={run.id}>
              <TableRow
                className="cursor-pointer hover:bg-slate-50"
                onClick={() =>
                  setExpandedId(expandedId === run.id ? null : run.id)
                }
              >
                <TableCell className="text-slate-500 font-mono text-sm">
                  {idx + 1}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {run.as_of ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
                      <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {new Date(run.as_of).toLocaleDateString("fr-FR")}
                    </span>
                  ) : (
                    <span className="text-xs italic text-slate-400">
                      <Trans>Tous les documents</Trans>
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs max-w-[150px] truncate">
                  {truncateJson(run.input_data)}
                </TableCell>
                <TableCell className="font-mono text-xs max-w-[150px] truncate">
                  {run.error_message ? (
                    <span className="text-red-500">
                      {truncateJson(run.error_message)}
                    </span>
                  ) : (
                    truncateJson(run.actual_output)
                  )}
                </TableCell>
                <TableCell>
                  <ExperimentStatusBadge status={run.status} />
                </TableCell>
                <TableCell className="text-sm">
                  {run.metadata_?.overall_score !== undefined &&
                  run.metadata_?.overall_score !== null ? (
                    <span
                      className={`font-semibold ${
                        run.metadata_.overall_score >= 0.8
                          ? "text-green-700"
                          : run.metadata_.overall_score >= 0.5
                            ? "text-primary"
                            : "text-red-600"
                      }`}
                    >
                      {(run.metadata_.overall_score * 100).toFixed(0)}%
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {run.metadata_?.n_merge_conflicts ? (
                    <Badge className="bg-blue-50 text-primary border border-primary/30 text-xs flex items-center gap-1 w-fit">
                      <AlertTriangle className="h-3 w-3" />
                      {run.metadata_.n_merge_conflicts}
                    </Badge>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {fmtMs(run.latency_ms)}
                </TableCell>
                <TableCell className="text-sm">
                  {run.total_tokens || "—"}
                </TableCell>
                <TableCell>
                  <ScoreBadges feedbacks={run.feedbacks || []} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId(expandedId === run.id ? null : run.id);
                      }}
                    >
                      {expandedId === run.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroundingRunId(run.id);
                      }}
                      title={t`Voir les sources dans les documents`}
                      disabled={!run.grounding_data && !run.actual_output}
                    >
                      <BookOpen className="h-4 w-4 text-primary" />
                    </Button>
                    {onValidate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onValidate(run.id);
                        }}
                        title={t`Valider l'extraction`}
                      >
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </Button>
                    )}
                    {onAddFeedback && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddFeedback(run.id);
                        }}
                        title={t`Ajouter un retour`}
                      >
                        <MessageSquarePlus className="h-4 w-4 text-primary" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              {expandedId === run.id && (
                <TableRow>
                  <TableCell colSpan={11} className="bg-slate-50 p-4">
                    {run.grounding_data && fields.length > 0 && (
                      <div className="mb-3">
                        <GroundingHeatmap
                          grounding={run.grounding_data}
                          fields={fields}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                          Entrée
                        </p>
                        <pre className="bg-white border border-slate-200 rounded p-3 text-xs overflow-auto max-h-48">
                          {JSON.stringify(run.input_data, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                          Sortie réelle
                        </p>
                        <pre className="bg-white border border-slate-200 rounded p-3 text-xs overflow-auto max-h-48">
                          {run.actual_output
                            ? JSON.stringify(run.actual_output, null, 2)
                            : run.error_message || "null"}
                        </pre>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                          Sortie attendue
                        </p>
                        <pre className="bg-white border border-slate-200 rounded p-3 text-xs overflow-auto max-h-48">
                          {run.expected_output
                            ? JSON.stringify(run.expected_output, null, 2)
                            : "null"}
                        </pre>
                      </div>
                    </div>

                    {/* Per-field detailed scores against the validated truth */}
                    {run.metadata_ && (
                      <FieldScoresTable
                        metadata={run.metadata_}
                        expected={run.expected_output}
                        actual={run.actual_output}
                      />
                    )}

                    {/* Merge conflicts (when extraction yielded ≥2 candidates) */}
                    {run.metadata_?.merge_conflicts &&
                      Object.keys(run.metadata_.merge_conflicts).length > 0 && (
                        <MergeConflictsList
                          conflicts={run.metadata_.merge_conflicts}
                        />
                      )}

                    {/* Feedbacks detail */}
                    {run.feedbacks && run.feedbacks.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-slate-700 mb-2">
                          Évaluations
                        </p>
                        <div className="space-y-2">
                          {run.feedbacks.map((fb) => (
                            <div
                              key={fb.id}
                              className="flex items-center gap-3 bg-white border border-slate-200 rounded p-2 text-sm"
                            >
                              {fb.source_type === "LLM_JUDGE" ? (
                                <Badge className="bg-blue-50 text-primary border border-primary/30 text-xs flex items-center gap-1">
                                  <Brain className="h-3 w-3 text-primary" />
                                  LLM Judge
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {fb.source_type}
                                </Badge>
                              )}
                              <span className="font-medium">{fb.key}</span>
                              {fb.score !== null && (
                                <Badge
                                  variant={
                                    fb.score >= 0.7
                                      ? "default"
                                      : fb.score >= 0.4
                                        ? "secondary"
                                        : "destructive"
                                  }
                                >
                                  {(fb.score * 100).toFixed(0)}%
                                </Badge>
                              )}
                              {fb.value && (
                                <span className="text-slate-600">{fb.value}</span>
                              )}
                              {fb.comment && (
                                <span className="text-slate-500 italic">
                                  {fb.comment}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>

      {/* Grounding sources modal */}
      {groundingRun && (
        <GroundingModal
          run={groundingRun}
          fields={fields}
          onClose={() => setGroundingRunId(null)}
        />
      )}
    </>
  );
}
